// src/admin/components/MasterAdminSidebar.js
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Users, 
  Settings, 
  Database,
  TrendingUp,
  Building2,
  Activity,
  Mail,
  FileText,
  Globe,
  Layers,
  Home,
  CreditCard,
  Tag,
  DollarSign,
  Shield,
  HelpCircle,
  Key
} from 'lucide-react';

const MasterAdminSidebar = ({ activeTab, setActiveTab }) => {
  const navigate = useNavigate();

  // Helper to handle navigation
  const handleNavClick = (tabId) => {
    if (setActiveTab) {
      setActiveTab(tabId);
    } else {
      navigate(`/adminhq/${tabId}`);
    }
  };

  // Core Administration Items
  const coreAdminItems = [
    { id: 'users', name: 'Users', icon: Users, color: 'green' },
    { id: 'traffic', name: 'Traffic', icon: TrendingUp, color: 'purple' },
    { id: 'logs', name: 'Logs', icon: Activity, color: 'teal' },
    { id: 'health', name: 'Health', icon: Activity, color: 'green' },
    { id: 'database', name: 'Database', icon: Database, color: 'red' },
    { id: 'settings', name: 'Settings', icon: Settings, color: 'gray' },
    { id: 'api-keys', name: 'API Keys', icon: Key, color: 'orange' },
  ];

  // Payments & Billing
  const paymentsItems = [
    { id: 'payments', name: 'Payments', icon: CreditCard, color: 'blue' },
    { id: 'create-invoice', name: 'New Invoice', icon: FileText, color: 'purple' },
    { id: 'plans', name: 'Subs Plans', icon: Layers, color: 'indigo' },
    { id: 'plan-restrictions', name: 'Plan Restrictions', icon: Settings, color: 'purple' },
    { id: 'promo-codes', name: 'Promo Codes', icon: Tag, color: 'green' },
    { id: 'email-logs', name: 'Email Log', icon: Mail, color: 'blue' },
  ];

  // CMS (Content Management) - Currently using mock data, hidden until fully functional
  // const cmsItems = [
  //   { id: 'email-templates', name: 'Email Templates', icon: Mail, color: 'blue' },
  //   { id: 'dataroom-templates', name: 'Dataroom Templates', icon: FileText, color: 'purple' },
  //   { id: 'import-templates', name: 'Import Templates', icon: FileText, color: 'teal' },
  // ];

  // Templates
  const templateItems = [
    { id: 'email-templates', name: 'Email Templates', icon: Mail, color: 'green' },
    { id: 'dataroom-templates', name: 'Dealroom Templates', icon: FileText, color: 'purple' },
    { id: 'import-templates', name: 'Import Templates', icon: FileText, color: 'teal' },
  ];

  // Product-level navigation (one URL per product)
  const productNavItems = [
    { id: 'equity', name: 'Equity', icon: Building2, color: 'blue' },
    { id: 'dealrooms', name: 'Dealrooms', icon: Shield, color: 'green' },
    { id: 'capraise', name: 'CapRaise', icon: TrendingUp, color: 'purple' },
    { id: 'questions', name: 'Questions', icon: HelpCircle, color: 'orange' },
    { id: 'userinvites', name: 'User Invites', icon: Users, color: 'teal' },
  ];

  // Data Management
  const dataItems = [
    { id: 'share-classes', name: 'Share Types', icon: Activity, color: 'blue' },
    { id: 'currencies', name: 'Currencies', icon: DollarSign, color: 'green' },
    { id: 'geo-data', name: 'Geographic Data', icon: Globe, color: 'orange' },
  ];



  const navLinkClasses = (isActive) =>
    `flex items-center px-4 py-3 text-sm font-medium rounded-md transition-colors ${
      isActive 
        ? 'bg-blue-100 text-blue-700 border-r-2 border-blue-500' 
        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
    }`;

  const sectionHeaderClasses = "px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider";

  return (
    <aside className="w-64 bg-white shadow-lg flex flex-col h-screen">
      {/* Header */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center">
          <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
            <span className="text-white font-bold text-sm">A</span>
          </div>
          <div className="ml-3">
            <h1 className="text-lg font-bold text-gray-900">AdminHQ</h1>
            <p className="text-xs text-gray-500">System Administration</p>
          </div>
        </div>
        
        {/* Product Selector */}
        <div className="mt-4">
          <label className="block text-xs font-medium text-gray-700 mb-2">
            Admin Product
          </label>
          <select
            onChange={(e) => {
              if (e.target.value) {
                navigate(`/adminhq/${e.target.value}`);
              }
            }}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
          >
            <option value="">Select Product to Admin</option>
            <option value="equity">Equity</option>
            <option value="dealrooms">Dealrooms</option>
            <option value="capraise">CapRaise</option>
            <option value="questions">Questions</option>
            <option value="userinvites">User Invites</option>
          </select>
        </div>
      </div>

      <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
        {/* Products */}
        <div className="mb-4">
          <h3 className={sectionHeaderClasses}>Products</h3>
          <ul className="space-y-1">
            {productNavItems.map(item => (
              <li key={item.id}>
                <button
                  onClick={() => handleNavClick(item.id)}
                  className={navLinkClasses(activeTab === item.id)}
                >
                  <item.icon className="h-5 w-5 mr-3" />
                  {item.name}
                </button>
              </li>
            ))}
          </ul>
        </div>

        <div className="border-t border-gray-200 my-4"></div>

        {/* Core Administration */}
        <div className="mb-4">
          <ul className="space-y-1">
            {coreAdminItems.map(item => (
              <li key={item.id}>
                <button
                  onClick={() => handleNavClick(item.id)}
                  className={navLinkClasses(activeTab === item.id)}
                >
                  <item.icon className="h-5 w-5 mr-3" />
                  {item.name}
                </button>
              </li>
            ))}
          </ul>
        </div>

        <div className="border-t border-gray-200 my-4"></div>

        {/* Payments & Billing */}
        <div className="mb-4">
          <h3 className={sectionHeaderClasses}>Payments</h3>
          <ul className="space-y-1">
            {paymentsItems.map(item => (
              <li key={item.id}>
                <button
                  onClick={() => handleNavClick(item.id)}
                  className={navLinkClasses(activeTab === item.id)}
                >
                  <item.icon className="h-5 w-5 mr-3" />
                  {item.name}
                </button>
              </li>
            ))}
          </ul>
        </div>

        <div className="border-t border-gray-200 my-4"></div>

        {/* CMS - Hidden until fully functional */}
        {/* <div className="mb-4">
          <h3 className={sectionHeaderClasses}>CMS</h3>
          <ul className="space-y-1">
            {cmsItems.map(item => (
              <li key={item.id}>
                <button
                  onClick={() => handleNavClick(item.id)}
                  className={navLinkClasses(activeTab === item.id)}
                >
                  <item.icon className="h-5 w-5 mr-3" />
                  {item.name}
                </button>
              </li>
            ))}
          </ul>
        </div>

        <div className="border-t border-gray-200 my-4"></div> */}

        {/* Templates */}
        <div className="mb-4">
          <h3 className={sectionHeaderClasses}>Templates</h3>
          <ul className="space-y-1">
            {templateItems.map(item => (
              <li key={item.id}>
                <button
                  onClick={() => handleNavClick(item.id)}
                  className={navLinkClasses(activeTab === item.id)}
                >
                  <item.icon className="h-5 w-5 mr-3" />
                  {item.name}
                </button>
              </li>
            ))}
          </ul>
        </div>

        <div className="border-t border-gray-200 my-4"></div>

        {/* Data Management */}
        <div className="mb-4">
          <h3 className={sectionHeaderClasses}>Data</h3>
          <ul className="space-y-1">
            {dataItems.map(item => (
              <li key={item.id}>
                <button
                  onClick={() => handleNavClick(item.id)}
                  className={navLinkClasses(activeTab === item.id)}
                >
                  <item.icon className="h-5 w-5 mr-3" />
                  {item.name}
                </button>
              </li>
            ))}
          </ul>
        </div>



        {/* Quick Actions */}
        <div className="border-t border-gray-200 pt-4">
          <h3 className={sectionHeaderClasses}>Quick Actions</h3>
          <ul className="space-y-1">
            <li>
              <button
                onClick={() => window.location.href = '/my-account/dashboard'}
                className="flex items-center px-4 py-3 text-sm font-medium text-gray-600 rounded-md hover:bg-gray-100 hover:text-gray-900 transition-colors"
              >
                <Home className="h-5 w-5 mr-3" />
                Back to My Account
              </button>
            </li>
          </ul>
        </div>
      </nav>
    </aside>
  );
};

export default MasterAdminSidebar;